/**
 * StructurePlacementModule — THE PLACEMENT MAP (SWG's structure placement, 2026-09-23).
 *
 * Deploy enters PLACEMENT MODE: the camera becomes a FACE-DOWN MAP over the player, north up,
 * that follows them as they walk; the structure's footprint is drawn as a wireframe outline on
 * the ground under the cursor with its DOOR marked on the −Z face and the approach point beyond
 * it; the outline is GREEN where the structure may stand, RED where the terrain refuses it (slope,
 * water, another structure in the way, a blocked door) and YELLOW where a non-terrain rule does
 * (a civic piece outside its city, a City Hall too close to another city, a rank gate). A right-
 * click turns it 90° clockwise (R 45°, the wheel 15°), a left-click places it when it is green,
 * Esc leaves the map. The judgement is `lib/placement_rules.ts` — a pure mirror of the server's
 * `placementProblem`, which re-judges every call (Hard Rule #1: the map advises, the reducer
 * decides). The map borrows the avatar's own camera (`atlas.placementView` parks the avatar's
 * pose): every post pass, HUD projection and listener keeps its perspective camera.
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { sfx } from '../lib/sfx.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { footprintOf, doorApproach, STRUCTURE_LABELS } from '@shared/types/structures.js';
import { terrainHeight, plateauSites } from '@shared/terrain.js';
import { claimsFor } from '../lib/guild_claims.js';
import { groundYIn } from '../lib/grounding.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { waterStillAt } from '../lib/water_fx.js';
import { serverStandingWater } from '../lib/placement_water.js';
import { loadedRivers } from '../lib/hydrology_load.js';
import { structureSolidsNear } from '../lib/structure_solids.js';
import {
  judgePlacement, footprintObb, rotateClockwise, rotateBy, viewHeightFor, groundUnderRay, worldToCell,
  PLACEMENT_HEX, REGION_SIZE_M, type PlacementWorld, type PlacementVerdict, type PlacedObb,
} from '../lib/placement_rules.js';

/** How often the ground rules are re-judged while the cursor rests (the walk moves the map under it). */
const JUDGE_HZ = 12;

interface Ghost {
  group: THREE.Group;
  outline: THREE.LineSegments;
  floor: THREE.Mesh;
  door: THREE.Mesh;
  step: THREE.Mesh;
  lineMat: THREE.LineBasicMaterial;
  floorMat: THREE.MeshBasicMaterial;
  doorMat: THREE.MeshBasicMaterial;
  stepMat: THREE.MeshBasicMaterial;
}

export class StructurePlacementModule implements IModule {
  readonly name = 'StructurePlacement';

  private app!: App;
  private scene!: THREE.Scene;
  private canvas!: HTMLCanvasElement;
  private ghost: Ghost | null = null;
  private playerRing: THREE.Mesh | null = null;
  private hud: HTMLElement | null = null;
  private active: { deedId: bigint; structureType: string; civicCityId: bigint | null; guildId: bigint | null } | null = null;
  private yawDeg = 0;
  /** The cursor in NDC (null = the pointer is off the canvas). */
  private ndc: { x: number; y: number } | null = null;
  /** The last judged spot (absolute) and its verdict. */
  private spot: { x: number; y: number; z: number } | null = null;
  private verdict: PlacementVerdict = { color: 'red', why: 'no ground under the cursor' };
  private judgeAt = 0;
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private readonly camMat = new THREE.Matrix4();
  private readonly camQuat = new THREE.Quaternion();
  private readonly rigQuat = new THREE.Quaternion();
  private readonly tmpV = new THREE.Vector3();
  private savedFov = 0;
  private vmWasVisible: boolean | null = null;
  private citiesSubscribed = false;
  private pointerMove = (e: PointerEvent): void => this.onPointerMove(e);
  private pointerLeave = (): void => { this.ndc = null; };
  private pointerDown = (e: PointerEvent): void => this.onPointerDown(e);
  private contextMenu = (e: Event): void => { if (this.active) e.preventDefault(); };
  private wheel = (e: WheelEvent): void => this.onWheel(e);
  private key = (e: KeyboardEvent): void => this.onKey(e);

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    this.canvas = app.renderer.native.domElement as HTMLCanvasElement;
    bus.on('atlas:structure-place', ({ deedId, structureType, cityId, civic, guildId }) => {
      this.start(BigInt(deedId), structureType, civic && cityId ? BigInt(cityId) : null, guildId ? BigInt(guildId) : null);
    });
    bus.on('atlas:world-cleared', () => this.cancel());
    // The map is posed AFTER the avatar's own pose (Animation) and before the render.
    app.world.addSystem(makeSystem('AtlasPlacementMap', Phase.RenderSub, 4, () => this.tick()));
    (window as unknown as { __placement?: unknown }).__placement = () => this.diag();
  }

  // ─── Mode ─────────────────────────────────────────────────────────────────────

  private start(deedId: bigint, structureType: string, civicCityId: bigint | null = null, guildId: bigint | null = null): void {
    this.cancel();
    const cam = this.camera();
    if (!cam) { bus.emit('atlas:status', { text: 'No camera to place with yet.' }); return; }
    this.active = { deedId, structureType, civicCityId, guildId };
    this.yawDeg = 0;
    this.ndc = null;
    this.spot = null;
    this.verdict = { color: 'red', why: 'move the cursor over the ground' };
    atlas.editorCursorMode = true;
    atlas.placementView = true;
    if (document.pointerLockElement) document.exitPointerLock();
    this.savedFov = cam.fov;
    // The gun in hand hangs off this camera: a map with a pistol across it is not a map ("move the camera, not
    // the character and the weapon in hand"). Hidden for the map, restored on exit (the avatar module's own
    // third-person rule takes over again once it poses the camera).
    const vm = cam.getObjectByName('weapon-view-model');
    this.vmWasVisible = vm ? vm.visible : null;
    if (vm) vm.visible = false;
    this.ensureCities();
    this.ghost = this.buildGhost(structureType);
    this.scene.add(this.ghost.group);
    this.playerRing = new THREE.Mesh(
      new THREE.RingGeometry(0.55, 0.8, 32).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ color: 0x5fd8ff, transparent: true, opacity: 0.9, depthTest: false, depthWrite: false }),
    );
    this.playerRing.renderOrder = 1001;
    this.scene.add(this.playerRing);
    this.hud = this.buildHud(structureType, civicCityId !== null, guildId !== null);
    this.canvas.addEventListener('pointermove', this.pointerMove);
    this.canvas.addEventListener('pointerleave', this.pointerLeave);
    this.canvas.addEventListener('pointerdown', this.pointerDown);
    this.canvas.addEventListener('contextmenu', this.contextMenu);
    this.canvas.addEventListener('wheel', this.wheel, { passive: false });
    // CAPTURE phase (2026-09-25): the map owns Esc — it must run before the panels' bubble-phase Esc handlers so it can
    // consume the press (the options menu's own capture listener yields to `atlas.placementView`).
    window.addEventListener('keydown', this.key, true);
    bus.emit('atlas:status', {
      text: `Placing ${label(structureType)} — green places, red is the terrain's no, yellow a rule's; right-click turns it, click to place, Esc leaves the map.`,
    });
  }

  private cancel(): void {
    if (!this.active) return;
    this.active = null;
    atlas.editorCursorMode = false;
    atlas.placementView = false;
    const cam = this.camera();
    if (cam && this.savedFov > 0 && Math.abs(cam.fov - this.savedFov) > 0.01) { cam.fov = this.savedFov; cam.updateProjectionMatrix(); }
    const vm = cam?.getObjectByName('weapon-view-model');
    if (vm && this.vmWasVisible !== null) vm.visible = this.vmWasVisible;
    this.vmWasVisible = null;
    if (this.ghost) {
      this.scene.remove(this.ghost.group);
      this.ghost.outline.geometry.dispose(); this.ghost.floor.geometry.dispose(); this.ghost.door.geometry.dispose(); this.ghost.step.geometry.dispose();
      this.ghost.lineMat.dispose(); this.ghost.floorMat.dispose(); this.ghost.doorMat.dispose(); this.ghost.stepMat.dispose();
      for (const c of this.ghost.group.children) if ((c as THREE.LineSegments).isLineSegments && c !== this.ghost.outline) ((c as THREE.LineSegments).material as THREE.Material).dispose();
      this.ghost = null;
    }
    if (this.playerRing) {
      this.scene.remove(this.playerRing);
      this.playerRing.geometry.dispose();
      (this.playerRing.material as THREE.Material).dispose();
      this.playerRing = null;
    }
    this.hud?.remove();
    this.hud = null;
    this.canvas.removeEventListener('pointermove', this.pointerMove);
    this.canvas.removeEventListener('pointerleave', this.pointerLeave);
    this.canvas.removeEventListener('pointerdown', this.pointerDown);
    this.canvas.removeEventListener('contextmenu', this.contextMenu);
    this.canvas.removeEventListener('wheel', this.wheel);
    window.removeEventListener('keydown', this.key, true);
  }

  private camera(): THREE.PerspectiveCamera | null {
    const cam = atlas.localMesh?.children[0];
    return cam && (cam as THREE.PerspectiveCamera).isPerspectiveCamera ? (cam as THREE.PerspectiveCamera) : null;
  }

  private ensureCities(): void {
    if (this.citiesSubscribed || !atlas.conn) return;
    this.citiesSubscribed = true;
    try { atlas.conn.subscriptionBuilder().subscribe(['SELECT * FROM city', 'SELECT * FROM no_build_zone', 'SELECT * FROM terrain_region']); } catch { /* pre-publish */ }
  }

  // ─── The ghost: outline + floor + door + approach step ───────────────────────────

  private buildGhost(type: string): Ghost {
    const fp = footprintOf(type);
    const w = fp.hx * 2, d = fp.hz * 2, h = fp.h;
    const lineMat = new THREE.LineBasicMaterial({ color: PLACEMENT_HEX.red, transparent: true, opacity: 0.95, depthTest: false, depthWrite: false });
    const floorMat = new THREE.MeshBasicMaterial({ color: PLACEMENT_HEX.red, transparent: true, opacity: 0.42, depthTest: false, depthWrite: false, side: THREE.DoubleSide });
    const doorMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9, depthTest: false, depthWrite: false, side: THREE.DoubleSide });
    const stepMat = new THREE.MeshBasicMaterial({ color: PLACEMENT_HEX.red, transparent: true, opacity: 0.85, depthTest: false, depthWrite: false, side: THREE.DoubleSide });
    const group = new THREE.Group();
    const box = new THREE.BoxGeometry(w, h, d);
    box.translate(0, h / 2, 0);
    const edges = new THREE.EdgesGeometry(box);
    const outline = new THREE.LineSegments(edges, lineMat);
    // A dark under-edge so the coloured lines read on pale ground as well as dark (the map's ground can be
    // sand, snow or a whiteout under a slow renderer); drawn just before the colour.
    const under = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.55, depthTest: false, depthWrite: false }));
    under.scale.setScalar(1.012);
    under.renderOrder = 999;
    box.dispose();
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(w, d).rotateX(-Math.PI / 2), floorMat);
    floor.position.y = 0.06;
    // The DOOR: a triangle on the −Z face pointing out of the building (the model's real entrance
    // faces this way — the set's model yaw put it there), wide enough to read on the map.
    const dw = Math.min(w * 0.35, 4), dl = Math.min(d * 0.3, 3);
    const tri = new THREE.BufferGeometry();
    tri.setAttribute('position', new THREE.Float32BufferAttribute([-dw / 2, 0, -fp.hz + dl, dw / 2, 0, -fp.hz + dl, 0, 0, -fp.hz - dl * 0.6], 3));
    tri.computeVertexNormals();
    const door = new THREE.Mesh(tri, doorMat);
    door.position.y = 0.09;
    // The APPROACH STEP: where a person stands to enter — judged on its own (dry, walkable, unblocked).
    const step = new THREE.Mesh(new THREE.RingGeometry(0.45, 0.7, 24).rotateX(-Math.PI / 2), stepMat);
    step.position.set(0, 0.09, -(fp.hz + 2.5));
    for (const m of [outline, floor, door, step]) m.renderOrder = 1000;
    group.add(under, outline, floor, door, step);
    group.visible = false;
    return { group, outline, floor, door, step, lineMat, floorMat, doorMat, stepMat };
  }

  private buildHud(type: string, civic: boolean, guild = false): HTMLElement {
    const el = document.createElement('div');
    el.id = 'placement-hud';
    el.setAttribute('data-atlas-placement', '1');
    Object.assign(el.style, {
      position: 'fixed', left: '50%', bottom: '18px', transform: 'translateX(-50%)', zIndex: '60', pointerEvents: 'none',
      padding: '10px 14px', borderRadius: 'var(--ax-radius)', background: 'color-mix(in srgb, var(--ax-bg) 86%, transparent)', color: 'var(--ax-text)',
      font: '13px/1.45 "IBM Plex Mono", ui-monospace, monospace', border: '1px solid color-mix(in srgb, var(--ax-accent) 55%, transparent)',
      minWidth: '360px', maxWidth: '560px', textAlign: 'center', whiteSpace: 'normal',
    } as Partial<CSSStyleDeclaration>);
    const line = (text: string, style: Partial<CSSStyleDeclaration>): HTMLElement => {
      const d = document.createElement('div'); Object.assign(d.style, style); d.textContent = text; el.appendChild(d); return d;
    };
    line(`PLACING · ${label(type)}${civic ? ' · civic' : guild ? ' · guild property' : ''}`, { fontWeight: '600', letterSpacing: '.04em', color: 'var(--ax-hi)' });
    line('', { margin: '4px 0', fontSize: '14px' }).setAttribute('data-verdict', '1');
    const legend = document.createElement('div'); legend.style.opacity = '.8';
    for (const [hex, text] of [['#3fb950', ' green places · '], ['#f85149', ' red: the ground refuses · '], ['#e3b341', ' yellow: a rule refuses']] as const) {
      const sw = document.createElement('span'); sw.style.color = hex; sw.textContent = '■'; legend.appendChild(sw); legend.appendChild(document.createTextNode(text));
    }
    el.appendChild(legend);
    line('right-click turns 90° · R 45° · wheel 15° · left-click places · Esc leaves the map · walk to move the map', { opacity: '.75', marginTop: '2px' });
    document.body.appendChild(el);
    return el;
  }

  // ─── Per frame: pose the map camera, seat the ghost, judge ───────────────────────

  private tick(): void {
    if (!this.active || !this.ghost) return;
    const cam = this.camera();
    const rig = atlas.localMesh;
    if (!cam || !rig) return;
    const conn = atlas.conn;
    // The map camera: straight down over the player from a height sized by the footprint, north up.
    const h = viewHeightFor(this.active.structureType);
    const px = atlas.localX + this.scene.position.x, pz = atlas.localZ + this.scene.position.z;
    const feetY = atlas.localY + this.scene.position.y;
    const eye = this.tmpV.set(px, feetY + h, pz);
    rig.updateMatrixWorld(true);
    rig.getWorldQuaternion(this.rigQuat);
    // World pose → rig-local (the camera is the rig's child).
    const local = rig.worldToLocal(eye.clone());
    cam.position.copy(local);
    this.camMat.lookAt(eye, new THREE.Vector3(px, feetY, pz), new THREE.Vector3(0, 0, -1));
    this.camQuat.setFromRotationMatrix(this.camMat);
    cam.quaternion.copy(this.rigQuat.invert().multiply(this.camQuat));
    if (Math.abs(cam.fov - 62) > 0.01) { cam.fov = 62; cam.updateProjectionMatrix(); }
    cam.updateMatrixWorld(true);
    // THE FRAME LAW: a scene child's position is ABSOLUTE (the FP64 rebase puts −origin on the scene itself),
    // so the ring and the outline take absolute coordinates; the CAMERA maths above is in render space.
    if (this.playerRing) this.playerRing.position.set(atlas.localX, atlas.localY + 0.05, atlas.localZ);
    // The cursor on the ground (render space → absolute for the rules).
    if (this.ndc && conn) {
      this.pointer.set(this.ndc.x, this.ndc.y);
      this.raycaster.setFromCamera(this.pointer, cam);
      const o = this.raycaster.ray.origin, dir = this.raycaster.ray.direction;
      const gy = (x: number, z: number) => groundYIn(conn, atlas.currentWorldId, x - this.scene.position.x, z - this.scene.position.z) + this.scene.position.y;
      const hit = groundUnderRay({ x: o.x, y: o.y, z: o.z }, { x: dir.x, y: dir.y, z: dir.z }, gy);
      if (hit) {
        this.spot = { x: hit.x - this.scene.position.x, y: hit.y - this.scene.position.y, z: hit.z - this.scene.position.z };
        this.ghost.group.visible = true;
        this.ghost.group.position.set(this.spot.x, this.spot.y, this.spot.z); // absolute — the ghost is a scene child
        this.ghost.group.rotation.y = THREE.MathUtils.degToRad(this.yawDeg);
        const now = performance.now();
        if (now - this.judgeAt > 1000 / JUDGE_HZ) { this.judgeAt = now; this.judge(); }
      } else { this.spot = null; this.ghost.group.visible = false; }
    } else { this.spot = null; this.ghost.group.visible = false; }
    this.paint();
  }

  /** The rules over the live world (absolute coordinates). */
  private judge(): void {
    if (!this.active || !this.spot) return;
    const conn = atlas.conn;
    if (!conn) { this.verdict = { color: 'red', why: 'not connected' }; return; }
    const world = this.worldFor(conn);
    const gid = this.active.guildId;
    const ownClaim = gid !== null ? (world.claims ?? []).find((c) => c.kind === 'player' && c.guildId === gid) : undefined;
    this.verdict = judgePlacement(world, {
      type: this.active.structureType, x: this.spot.x, z: this.spot.z,
      yaw: THREE.MathUtils.degToRad(this.yawDeg), civicCityId: this.active.civicCityId,
      guildId: gid, guildTier: ownClaim ? this.claimTier(conn, gid!) : 1,
    });
  }

  private worldFor(conn: NonNullable<typeof atlas.conn>): PlacementWorld {
    const worldId = atlas.currentWorldId;
    let seed = '';
    let commons = false;
    for (const w of conn.db.world.iter()) {
      if (worldId !== null && w.worldId === worldId) { commons = w.kind === 'commons'; seed = w.terrainSeed; break; }
    }
    if (!seed) for (const w of conn.db.world.iter()) if (w.kind === 'commons') { commons = true; seed = w.terrainSeed; break; }
    // The SERVER's ground on the Commons (the analytic terrain, the slope rule's own function); the rendered
    // ground elsewhere.
    const groundY = commons && seed
      ? (x: number, z: number) => terrainHeight(seed, x, z)
      : (x: number, z: number) => groundYIn(conn, worldId, x, z);
    const seaLevel = commons && seed ? commonsSeaLevel(seed) : null;
    let waterRuleOn = true;
    try { waterRuleOn = (conn.db as unknown as { platformFlag?: { key: { find(k: string): { enabled: boolean } | undefined } } }).platformFlag?.key.find('placement_water_v2')?.enabled !== false; } catch { waterRuleOn = true; }
    const structures = [...conn.db.structure.iter()] as Array<{ posX: number; posZ: number; yaw: number; structureType: string; cellX: number; cellZ: number }>;
    const db = conn.db as unknown as {
      noBuildZone?: { iter(): Iterable<{ cellX: number; cellZ: number; worldId?: bigint }> };
      terrainRegion?: { iter(): Iterable<{ regionX: number; regionZ: number; noBuild: boolean; worldId?: bigint }> };
      city?: { iter(): Iterable<{ cityId: bigint; centerX: number; centerZ: number; radiusM: number; cityRank: number; status?: string }> };
    };
    // `status` rides along (2026-09-25): the guild-hall border test skips a city out of standing, as the server does.
    const cities = db.city ? [...db.city.iter()].map((c) => ({ cityId: c.cityId, centerX: c.centerX, centerZ: c.centerZ, radiusM: c.radiusM, cityRank: c.cityRank, name: (c as { name?: string }).name ?? '', status: c.status ?? '' })) : [];
    // GUILD LAND (2026-09-24): the claims, the plateaus a claim keeps off, and whose buildings stand where.
    const claims = commons ? claimsFor(conn, atlas.conn?.identity ?? null) : [];
    const plateaus = commons && seed ? plateauSites(seed).filter((p) => p.r > 0).map((p) => ({ x: p.x, z: p.z, r: p.r + p.ring * 0.5 })) : [];
    const me = atlas.conn?.identity ?? null;
    const strangersOn = (x: number, z: number, r: number): number => {
      let n = 0;
      for (const st of conn.db.structure.iter() as Iterable<{ owner: { isEqual(o: unknown): boolean }; posX: number; posZ: number; yaw: number; structureType: string }>) {
        if (Math.abs(st.posX - x) > r + 70 || Math.abs(st.posZ - z) > r + 70) continue;
        if (me && st.owner.isEqual(me)) continue;
        const fp = footprintOf(st.structureType);
        const c = Math.cos(st.yaw), sn = Math.sin(st.yaw), dx = x - st.posX, dz = z - st.posZ;
        const lx = c * dx - sn * dz, lz = sn * dx + c * dz;
        const qx = Math.max(Math.abs(lx) - fp.hx, 0), qz = Math.max(Math.abs(lz) - fp.hz, 0);
        if (qx * qx + qz * qz <= r * r) n++;
      }
      return n;
    };
    return {
      groundY,
      seaLevel,
      waterAt: (x, z) => (commons ? waterStillAt(x, z) : null),
      // PONDS, LAKES AND RIVERS AS THE SERVER REFUSES THEM (2026-10-01): the same rule on the same inputs, so the map is
      // never green where `placementProblem` says "in a pond". The lever is the server's (`platform_flag
      // placement_water_v2`, ABSENT = ON): with it off the hook is absent and the judge is the old judge.
      standingWaterAt: commons && seed && waterRuleOn ? serverStandingWater(seed, loadedRivers(seed)) : undefined,
      obstaclesNear: (x, z, reach) => {
        const out: PlacedObb[] = [];
        for (const s of structures) {
          if (Math.abs(s.posX - x) > reach + 40 || Math.abs(s.posZ - z) > reach + 40) continue;
          out.push({ ...footprintObb(s.structureType, s.posX, s.posZ, s.yaw), type: s.structureType });
        }
        // What only the client knows: settlement buildings, dressing, the sky-ship hulls (the solids registry).
        for (const sd of structureSolidsNear(x, z, reach + 40)) out.push({ x: sd.x, z: sd.z, hx: sd.hx, hz: sd.hz, yaw: sd.yaw, type: 'solid' });
        return out;
      },
      noBuildAt: (x, z, cx, cz) => {
        if (db.noBuildZone) for (const zn of db.noBuildZone.iter()) if (zn.cellX === cx && zn.cellZ === cz && (zn.worldId === undefined || worldId === null || zn.worldId === worldId)) return true;
        return false;
      },
      // A protected region is its own reason, judged before the zone cell (2026-09-25: the server says "a protected
      // region"; folded into noBuildAt the map said "a no-build zone").
      protectedAt: (x, z) => {
        if (!db.terrainRegion) return false;
        const rx = Math.floor(x / REGION_SIZE_M), rz = Math.floor(z / REGION_SIZE_M);
        for (const r of db.terrainRegion.iter()) if (r.regionX === rx && r.regionZ === rz && r.noBuild && (r.worldId === undefined || worldId === null || r.worldId === worldId)) return true;
        return false;
      },
      cities,
      claims,
      plateaus,
      strangersOn,
    };
  }

  /** The tier of a player guild's claim (1 when unknown). */
  private claimTier(conn: NonNullable<typeof atlas.conn>, guildId: bigint): number {
    const t = (conn.db as unknown as { guildTerritory?: { territoryKey: { find(k: string): { tier: number } | undefined } } }).guildTerritory;
    try { return t?.territoryKey.find(`p:${guildId}`)?.tier ?? 1; } catch { return 1; }
  }

  private paint(): void {
    if (!this.ghost) return;
    const hex = PLACEMENT_HEX[this.verdict.color];
    this.ghost.lineMat.color.setHex(hex);
    this.ghost.floorMat.color.setHex(hex);
    const doorBad = /door/.test(this.verdict.why);
    this.ghost.stepMat.color.setHex(doorBad ? PLACEMENT_HEX.red : this.verdict.color === 'green' ? PLACEMENT_HEX.green : hex);
    if (this.hud) {
      const v = this.hud.querySelector('[data-verdict]') as HTMLElement | null;
      if (v) {
        const word = this.verdict.color.toUpperCase();
        const text = !this.spot ? 'move the cursor over the ground' : this.verdict.color === 'green' ? 'GREEN — it can stand here' : `${word} — ${this.verdict.why}`;
        if (v.textContent !== text) { v.textContent = text; v.style.color = `#${hex.toString(16).padStart(6, '0')}`; }
      }
    }
  }

  // ─── Input ────────────────────────────────────────────────────────────────────

  private onPointerMove(e: PointerEvent): void {
    if (!this.active) return;
    const r = this.canvas.getBoundingClientRect();
    this.ndc = { x: ((e.clientX - r.left) / r.width) * 2 - 1, y: -((e.clientY - r.top) / r.height) * 2 + 1 };
  }

  private onPointerDown(e: PointerEvent): void {
    if (!this.active) return;
    if (e.button === 2) { // SWG: a right-click turns the footprint 90° clockwise on the map
      e.preventDefault();
      this.yawDeg = rotateClockwise(this.yawDeg);
      this.judgeAt = 0;
      return;
    }
    if (e.button !== 0 || !this.ghost?.group.visible || !this.spot) return;
    const conn = atlas.conn;
    if (!conn) return;
    // Judge NOW at the click's spot (the cadence may be a frame behind the cursor).
    this.judge();
    if (this.verdict.color !== 'green') {
      const text = `Cannot place it there — ${this.verdict.why}.`;
      bus.emit('atlas:status', { text });
      bus.emit('atlas:toast', { text, kind: 'error' }); // the reason, in front of the player, for 5 s
      sfx('sfx_error', { gain: 0.35 });
      return;
    }
    const { deedId, structureType, civicCityId, guildId } = this.active;
    const posX = this.spot.x, posZ = this.spot.z;
    // RADIANS on the wire (2026-09-14): the renderer, the collider and the interior read `structure.yaw`
    // straight into rotation.y.
    const yaw = THREE.MathUtils.degToRad(this.yawDeg);
    this.cancel();
    bus.emit('atlas:status', { text: `Deploying ${label(structureType)}…` });
    // CIVIC PLACEMENT (2026-09-23): a city's civic / decor deed goes through placeCivicStructure (the mayor,
    // militia or a zoning grant; rank-gated; inside the city radius) — the server judges the same ground rules.
    const r = conn.reducers as unknown as {
      deployStructure(a: { deedId: bigint; posX: number; posZ: number; yaw: number }): Promise<void>;
      placeCivicStructure(a: { cityId: bigint; deedId: bigint; posX: number; posZ: number; civicType: string; yaw: number }): Promise<void>;
      placeGuildStructure(a: { guildId: bigint; deedId: bigint; posX: number; posZ: number; yaw: number }): Promise<void>;
    };
    // GUILD PROPERTY (2026-09-24): the court raises a building on the guild's claim — the same ground rules.
    const call = guildId !== null
      ? r.placeGuildStructure({ guildId, deedId, posX, posZ, yaw })
      : civicCityId
        ? r.placeCivicStructure({ cityId: civicCityId, deedId, posX, posZ, civicType: structureType, yaw })
        : r.deployStructure({ deedId, posX, posZ, yaw });
    void call
      .then(() => {
        sfx('sfx_structure_place', { gain: 0.5 });
        bus.emit('atlas:status', { text: 'Structure deployed.' });
        bus.emit('atlas:toast', { text: `${label(structureType)} placed.`, kind: 'success', ms: 3000 });
      })
      .catch((err: Error) => {
        // The server's own reason — "Not enough lots", "Cannot deploy here — in the water" — for 5 s.
        const why = err.message.replace(/^SenderError:\s*/, '').slice(0, 160);
        bus.emit('atlas:status', { text: `Deploy rejected: ${why}` });
        bus.emit('atlas:toast', { text: why, kind: 'error' });
      });
  }

  private onWheel(e: WheelEvent): void {
    if (!this.active) return;
    e.preventDefault();
    this.yawDeg = rotateBy(this.yawDeg, e.deltaY > 0 ? -15 : 15);
    this.judgeAt = 0;
  }

  private onKey(e: KeyboardEvent): void {
    if (!this.active) return;
    if (e.code === 'Escape') {
      // ESC PRECEDENCE (2026-09-25): an open options menu closes first (it consumed the press: preventDefault); a field
      // being typed in keeps its own Esc; otherwise Esc leaves the map and is CONSUMED — the options menu never opened
      // on the same press as the map closed.
      if (e.defaultPrevented || e.repeat) return;
      const tag = (document.activeElement as HTMLElement | null)?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      e.preventDefault();
      e.stopPropagation();
      this.cancel();
      bus.emit('atlas:status', { text: 'Placement cancelled.' });
    } else if (e.code === 'KeyR') {
      const tag = (document.activeElement as HTMLElement | null)?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return; // an R typed in the chat is a letter, not a turn
      e.preventDefault();
      this.yawDeg = rotateBy(this.yawDeg, 45);
      this.judgeAt = 0;
    }
  }

  /** Probe lever: the map's state, a judgement at any absolute spot, and the controls. */
  private diag(): unknown {
    const cam = this.camera();
    const dir = new THREE.Vector3();
    cam?.getWorldDirection(dir);
    const fp = this.active ? footprintOf(this.active.structureType) : null;
    return {
      active: this.active ? { type: this.active.structureType, deedId: this.active.deedId.toString(), civic: this.active.civicCityId?.toString() ?? null } : null,
      yawDeg: this.yawDeg,
      spot: this.spot,
      verdict: this.verdict,
      ghostVisible: !!this.ghost?.group.visible,
      /** The outline's drawn (render-space) position and the origin shift — a probe pins outline == spot + scene.position. */
      ghostRender: this.ghost ? this.ghost.group.getWorldPosition(new THREE.Vector3()).toArray() : null,
      originShift: this.scene.position.toArray(),
      cameraDown: dir.y,
      viewModelVisible: cam?.getObjectByName('weapon-view-model')?.visible ?? null,
      bodyVisible: atlas.localMesh?.getObjectByName('local-body')?.visible ?? null,
      cameraHeightOverFeet: cam ? cam.getWorldPosition(new THREE.Vector3()).y - (atlas.localY + this.scene.position.y) : null,
      footprint: fp,
      door: this.spot && fp ? doorApproach(this.spot.x, this.spot.z, THREE.MathUtils.degToRad(this.yawDeg), fp.hz) : null,
      cell: this.spot ? [worldToCell(this.spot.x), worldToCell(this.spot.z)] : null,
      judgeAt: (x: number, z: number, yawDeg = this.yawDeg, type = this.active?.structureType ?? 'small_house') => {
        const conn = atlas.conn;
        if (!conn) return null;
        return judgePlacement(this.worldFor(conn), { type, x, z, yaw: THREE.MathUtils.degToRad(yawDeg), civicCityId: this.active?.civicCityId ?? null });
      },
      setCursor: (x: number, z: number) => { // absolute → NDC through the live camera
        const c = this.camera(); if (!c) return false;
        const p = new THREE.Vector3(x + this.scene.position.x, groundYIn(atlas.conn!, atlas.currentWorldId, x, z) + this.scene.position.y, z + this.scene.position.z).project(c);
        this.ndc = { x: p.x, y: p.y }; this.judgeAt = 0; return Math.abs(p.x) <= 1 && Math.abs(p.y) <= 1;
      },
      rotateCw: () => { this.yawDeg = rotateClockwise(this.yawDeg); this.judgeAt = 0; return this.yawDeg; },
      place: () => { if (this.spot) this.onPointerDown(new PointerEvent('pointerdown', { button: 0 })); },
      cancel: () => this.cancel(),
      /** Enter the map directly (a probe's lever; the game goes through 'atlas:structure-place'). */
      start: (deedId: string, type: string, cityId?: string) => this.start(BigInt(deedId), type, cityId ? BigInt(cityId) : null),
    };
  }

  dispose(): void { this.cancel(); }
}

function label(type: string): string { return STRUCTURE_LABELS[type] ?? type.replace(/_/g, ' '); }
